import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import MainLayout from '@/views/layouts/MainLayout.vue'
import organizerFormatsRoutes from '@/router/organizer/organizer-formats-routes.ts'
import { useAuthStore } from '@/stores/auth.store'
import { useNavContextStore } from '@/stores/nav-context.store'
import { useErrorPageStore } from '@/stores/error-page.store'
import staticViews from '@/router/static-views.ts'
import { useTitle } from '@vueuse/core'
import i18next from '@/common/i18n'

import { ref } from 'vue'
import { isUnderAge } from '@/utils/calculateAge'
import { getDefaultAuthenticatedRoute } from '@/utils/authRedirect'

const routes: RouteRecordRaw[] = [
    {
        path: '/explore',
        name: 'explore',
        meta: {
            title: 'ui.explore.title',
            robots: 'index, follow',
        },
        component: () => import('@/views/ExploreView.vue'),
    },
    {
        path: '/eindruecke',
        name: 'testimonials',
        meta: {
            title: 'frontend.testimonials.pageTitle',
            robots: 'index, follow',
        },
        component: () => import('@/views/testimonials/TestimonialsView.vue'),
    },
    {
        path: '/events',
        name: 'events',
        component: () => import('@/views/events/EventsView.vue'),
        meta: {
            robots: 'index, follow',
        },
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('@/views/RegisterView.vue'),
        meta: {
            disableSubNavigation: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => import('@/views/profile/ProfileView.vue'),
        meta: { requiresAuth: true, title: 'ui.profile.title', robots: 'noindex, nofollow' },
    },
    {
        path: '/profile/applications',
        name: 'participant-applications',
        meta: {
            requiresAuth: true,
            roles: ['participant'],
            title: 'ui.navigation.applications',
            robots: 'noindex, nofollow',
        },
        component: () => import('@/views/ParticipantApplicationsView.vue'),
    },
    {
        path: '/organizer/formats/:formatId/participation-schedule',
        name: 'organizer-format-participation-schedule',
        meta: { requiresAuth: true, roles: ['organizer'], robots: 'noindex, nofollow' },
        component: () => import('@/views/organizer/ParticipationScheduleView.vue'),
        props: true,
    },
    {
        path: '/profile/participations',
        name: 'participant-participations',
        meta: { requiresAuth: true, roles: ['participant'], robots: 'noindex, nofollow' },
        component: () => import('@/views/ParticipationsView.vue'),
    },
    {
        path: '/profile/watchlist',
        name: 'watchlist',
        meta: {
            requiresAuth: true,
            roles: ['participant', 'organizer'],
            robots: 'noindex, nofollow',
        },
        component: () => import('@/views/WatchlistView.vue'),
    },
    // Intentionally placed outside the /participant subtree: this is a standalone
    // internal view for participants and must not be wrapped by ChapterizedLayout.
    {
        path: '/participant/formats/:formatId/intern',
        name: 'participant-format-intern',
        meta: { requiresAuth: true, roles: ['participant'], robots: 'noindex, nofollow' },
        component: () => import('@/views/ParticipantFormatInternView.vue'),
        props: true,
    },
    {
        path: '/profile/notifications',
        name: 'notifications',
        meta: {
            requiresAuth: true,
            roles: ['participant', 'organizer'],
            robots: 'noindex, nofollow',
        },
        component: () => import('@/views/NotificationsView.vue'),
    },
    {
        path: '/formats/:slug',
        name: 'formatDetails',
        meta: {
            robots: 'index, follow',
        },
        component: () => import('@/views/formats/FormatDetails/FormatDetailsView.vue'),
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/LoginView.vue'),
        meta: {
            disableFooter: true,
            robots: 'noindex, nofollow',
        },
        beforeEnter: (to) => {
            const auth = useAuthStore()
            if (!auth.user) return true

            const { redirect } = to.query
            if (typeof redirect === 'string' && redirect !== '/login') {
                return { path: redirect }
            }
            return getDefaultAuthenticatedRoute(auth.user.role?.type)
        },
    },
    {
        path: '/organizer-register',
        name: 'organizer',
        component: () => import('@/views/LandingPageView.vue'),
        props: { role: 'organizer' },
        meta: {
            guestOnly: true,
            title: 'ui.organizer.landing.description',
            disableFooter: true,
            disableSubNavigation: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/request-password-reset-pin',
        name: 'request-password-reset-pin',
        component: () => import('@/views/RequestPasswordResetPinView.vue'),
        meta: {
            guestOnly: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('@/views/PasswordResetView.vue'),
        meta: {
            guestOnly: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/new-password',
        name: 'new-password',
        component: () => import('@/views/NewPasswordView.vue'),
        meta: {
            guestOnly: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/verify-email',
        name: 'verify-email',
        component: () => import('@/views/VerifyEmailView.vue'),
        meta: {
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/register/organizer',
        name: 'register-organizer',
        component: () => import('@/components/registration/OrganizerForm.vue'),
        meta: {
            disableSubNavigation: true,
            disableFooter: true,
            guestOnly: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/register/company',
        name: 'register-company',
        component: () => import('@/components/registration/CompanyForm.vue'),
        meta: {
            requiresAuth: true,
            roles: ['organizer'],
            disableSubNavigation: true,
            disableFooter: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/register/participant',
        name: 'register-participant',
        component: () => import('@/views/registration/ParticipantForm.vue'),
        meta: {
            disableFooter: true,
            disableSubNavigation: true,
            guestOnly: true,
            robots: 'noindex, nofollow',
        },
    },
    {
        path: '/guardian-consent/:token',
        name: 'guardian-consent-approval',
        component: () => import('@/views/registration/GuardianConsentApproval.vue'),
        meta: {
            disableSubNavigation: true,
            disableFooter: true,
            robots: 'noindex, nofollow',
        },
        props: true,
    },
    {
        path: '/formats/:formatDocumentId/job-postings/:jobPostingId',
        name: 'job-posting-detail',
        component: () => import('@/views/JobPostingDetailView.vue'),
        props: true,
        meta: {
            robots: 'index, follow',
        },
    },
    {
        path: '/formats/:formatDocumentId/job-postings/:jobPostingId/application-form/:applicationFormId',
        name: 'application-form',
        component: () => import('@/views/ApplicationFormView.vue'),
        props: true,
        meta: {
            robots: 'index, follow',
        },
    },
    {
        path: '/:path(.*)*',
        name: 'page',
        meta: {
            robots: 'index, follow',
        },
        component: () => import('@/views/CMSPageView.vue'),
    },
    {
        path: '/external-redirect',
        name: 'external-redirect',
        component: () => import('@/views/ExternalRedirectView.vue'),
        meta: {
            robots: 'noindex, nofollow',
        },
    },
]

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior(to, _from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        }
        if (to.hash) {
            return { el: to.hash, behavior: 'smooth' }
        }
        return { top: 0 }
    },
    routes: [
        {
            path: '/',
            name: 'main-layout',
            component: MainLayout,
            children: [...staticViews, ...routes],
        },
        organizerFormatsRoutes,
        {
            path: '/participant',
            meta: { requiresAuth: true, roles: ['participant'] },
            children: [
                {
                    path: '',
                    component: MainLayout,
                    children: [
                        {
                            path: '',
                            name: 'participant',
                            component: () => import('@/views/LandingPageView.vue'),
                            props: { role: 'participant' },
                        },
                        {
                            path: 'guardian-consent',
                            name: 'guardian-consent',
                            component: () => import('@/views/registration/GuardianConsent.vue'),
                            meta: {
                                disableSubNavigation: true,
                                disableFooter: true,
                            },
                        },
                    ],
                },
                {
                    path: 'formats/:formatId',
                    name: 'participant-formats',
                    component: () =>
                        import('@/views/applications/participantApplication/ParticipantApplicationLayout.vue'),
                    children: [
                        {
                            path: 'application',
                            name: 'participant-application-form',
                            component: () =>
                                import('@/views/applications/participantApplication/ParticipantApplication.vue'),
                            props: true,
                        },
                    ],
                },
            ],
        },
    ],
})

const pageTitle = ref('')
useTitle(pageTitle)

router.beforeEach((to) => {
    let title = ''
    if ('title' in to.meta) {
        title += i18next.t(to.meta.title as string) + ' | '
    }
    title += i18next.t('ui.appName')
    pageTitle.value = title

    const auth = useAuthStore()
    const requiresAuth = Boolean(to.meta.requiresAuth)
    const guestOnly = Boolean(to.meta.guestOnly)

    if (guestOnly && auth.user) {
        return getDefaultAuthenticatedRoute(auth.user.role?.type)
    }
    if (requiresAuth && !auth.user) {
        return {
            name: 'login',
            query: { redirect: to.fullPath },
        }
    }

    // Force organizers without a company relation through the company registration flow.
    // This also covers auto-login after email verification because the redirect to profile
    // will be intercepted here.
    const needsCompanyRegistration =
        auth.user?.role?.type === 'organizer' && auth.organizer !== null && !auth.organizer.company

    if (needsCompanyRegistration && to.name !== 'register-company') {
        return { name: 'register-company' }
    }

    return true
})

router.beforeEach((to) => {
    const { participant, user } = useAuthStore()

    if (!user || !user.birthday) return true

    const isMinor = isUnderAge(user.birthday, 16)

    if (!isMinor) return true

    const allowedRoutes = ['guardian-consent', 'verify-email', 'guardian-consent-approval']

    if (!participant?.legalGuardianApprovedAt && !allowedRoutes.includes(to.name as string)) {
        return { name: 'guardian-consent' }
    }

    if (participant?.legalGuardianApprovedAt && to.name === 'guardian-consent') {
        return { name: 'home' }
    }

    return true
})

// WHY: Reviewers may inspect formats in the frontend, but format changes must be made in Strapi.
router.beforeEach((to) => {
    const auth = useAuthStore()

    if (!auth.isReviewer) return true

    const isFormatsOverview = to.path === '/organizer/formats'
    const isFormatPreview =
        /^\/organizer\/formats\/[^/]+\/format$/.test(to.path) && to.query.preview === 'format'

    if (!isFormatsOverview && !isFormatPreview) {
        return { name: 'formats' }
    }

    return true
})

router.afterEach((to) => {
    const navContext = useNavContextStore()

    if (to.path.startsWith('/organizer') || Boolean(to.meta.isOrganizerSection)) {
        navContext.setContext('organizer')
    } else if (to.name === 'home' || to.name === 'explore' || to.path.startsWith('/participant')) {
        navContext.setContext('participant')
    }
})

// WHY: Failed lazy-loaded chunks or exceptions in guards would otherwise leave a blank page.
// The error is rendered in place (see App.vue), so the URL is not changed.
router.onError(() => {
    useErrorPageStore().showError(500)
})

export default router
